import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import { Box, ToggleButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useLocalizedWeekDays } from "../../hooks/useDateLocalization";
import { INK, NEUTRAL } from "../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { type DayPreference, ShiftType } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";

interface ShiftsListProps {
	userId?: string;
	onPreferencesChange?: (preferences: DayPreference[]) => void;
	variant?: "simple" | "preferences";
}

export function ShiftsList({ userId, onPreferencesChange, variant = "simple" }: ShiftsListProps) {
	const { t, i18n } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const [preferences, setPreferences] = useState<DayPreference[]>([]);
	const weekdayNamesLocalized = useLocalizedWeekDays("cccc");

	// Get a reference date for the current week (luxon global locale is already set by i18n)
	const weekStartDate = useMemo(() => DateTime.now().startOf("week"), [i18n.language]);

	useEffect(() => {
		if (variant === "preferences" && chosenDepartment?.department_id && userId) {
			const fetchPreferences = async () => {
				try {
					trackEvent("shifts_list_fetch_preferences_started", {
						user_id: userId,
						department_id: chosenDepartment.department_id,
					});

					const response = await axiosInstance.get<{ day: string; type: string; week_number: number }[]>(
						`/team/departments/${chosenDepartment.department_id}/nurse/weekly_preferences`,
					);

					if (response.status === 200) {
						const backendPrefs = response.data;
						const frontendPrefs: DayPreference[] = [];

						backendPrefs.forEach((pref) => {
							const days = Array.from({ length: 7 }, (_, i) => DateTime.now().set({ day: i }).setLocale("en").toFormat("cccc"));
							const dayIndex = days.indexOf(pref.day);

							if (dayIndex !== -1) {
								const existingPref = frontendPrefs.find((p) => p.weekIndex === pref.week_number && p.dayIndex === dayIndex);

								if (existingPref) {
									if (pref.type === ShiftType.Day) {
										existingPref.preferDay = true;
									} else if (pref.type === ShiftType.Night) {
										existingPref.preferNight = true;
									}
								} else {
									frontendPrefs.push({
										weekIndex: pref.week_number,
										dayIndex,
										preferDay: pref.type === ShiftType.Day,
										preferNight: pref.type === ShiftType.Night,
									});
								}
							}
						});

						setPreferences(frontendPrefs);

						trackEvent("shifts_list_fetch_preferences_success", {
							user_id: userId,
							department_id: chosenDepartment.department_id,
							preference_count: frontendPrefs.length,
						});
					}
				} catch (error) {
					console.error("Error fetching preferences:", error);
					showErrorSnackbar(
						t("failed.fetch.preferences"),
						{
							action: "failed to fetch preferences",
							nurse_id: userId,
						},
						error,
					);
					trackErrorEvent("shifts_list_fetch_preferences_failed", error, {
						user_id: userId,
						department_id: chosenDepartment.department_id,
					});
				}
			};

			fetchPreferences();
		}
	}, [chosenDepartment?.department_id, userId, variant, weekStartDate]);

	useEffect(() => {
		if (variant === "preferences") {
			onPreferencesChange?.(preferences);

			if (preferences.length > 0) {
				trackEvent("shifts_list_preferences_updated", {
					user_id: userId,
					preference_count: preferences.length,
					day_preferences: preferences.filter((p) => p.preferDay).length,
					night_preferences: preferences.filter((p) => p.preferNight).length,
				});
			}
		}
	}, [preferences, onPreferencesChange, variant, userId]);

	const handlePreferenceChange = async ({
		weekIndex,
		dayIndex,
		preferenceType,
	}: {
		weekIndex: number;
		dayIndex: number;
		preferenceType: ShiftType.Day | ShiftType.Night;
	}) => {
		if (!chosenDepartment?.department_id || !userId) return;

		// Get the English day name for the backend (backend expects "Monday", "Tuesday", etc.)
		const dayDate = weekStartDate.plus({ days: dayIndex });
		const dayName = dayDate.setLocale("en").toFormat("cccc");
		const currentPreference = getPreference(weekIndex, dayIndex);
		const isAlreadySelected = preferenceType === ShiftType.Day ? currentPreference.preferDay : currentPreference.preferNight;

		trackEvent("shifts_list_preference_toggle_clicked", {
			user_id: userId,
			department_id: chosenDepartment.department_id,
			day: dayName,
			week_index: weekIndex,
			preference_type: preferenceType,
			is_removing: isAlreadySelected,
		});

		try {
			trackEvent("shifts_list_preference_update_started", {
				user_id: userId,
				department_id: chosenDepartment.department_id,
				day: dayName,
				week_index: weekIndex,
				preference_type: preferenceType,
				action: isAlreadySelected ? "remove" : "add",
			});

			let response;
			if (isAlreadySelected) {
				response = await axiosInstance.delete(`/team/departments/${chosenDepartment.department_id}/nurse/${userId}/weekly_preferences`, {
					params: {
						day: dayName,
						shift_type: preferenceType,
						week_number: weekIndex,
					},
				});
			} else {
				response = await axiosInstance.post(
					`/team/departments/${chosenDepartment.department_id}/nurse/weekly_preferences`,
					{
						day: dayName,
						shift_type: preferenceType,
						exclusive: false,
						week_number: weekIndex,
					},
					{
						params: {
							user_id: userId,
						},
					},
				);
			}

			if (response.status === 200) {
				showSnackbar(isAlreadySelected ? t("preference.removed") : t("preference.updated"), "success");

				trackEvent("shifts_list_preference_update_success", {
					user_id: userId,
					department_id: chosenDepartment.department_id,
					day: dayName,
					week_index: weekIndex,
					preference_type: preferenceType,
					action: isAlreadySelected ? "remove" : "add",
				});

				setPreferences((prev: DayPreference[]) => {
					const existingPrefIndex = prev.findIndex((p: DayPreference) => p.weekIndex === weekIndex && p.dayIndex === dayIndex);

					if (existingPrefIndex >= 0) {
						const newPrefs = [...prev];
						if (preferenceType === ShiftType.Day) {
							newPrefs[existingPrefIndex].preferDay = !newPrefs[existingPrefIndex].preferDay;
						} else {
							newPrefs[existingPrefIndex].preferNight = !newPrefs[existingPrefIndex].preferNight;
						}
						if (!newPrefs[existingPrefIndex].preferDay && !newPrefs[existingPrefIndex].preferNight) {
							newPrefs.splice(existingPrefIndex, 1);
						}
						return newPrefs;
					}
					return [
						...prev,
						{
							weekIndex,
							dayIndex,
							preferDay: preferenceType === ShiftType.Day,
							preferNight: preferenceType === ShiftType.Night,
						},
					];
				});
			} else {
				showSnackbar(t("failed.update.preference"), "error");

				trackErrorEvent(
					"shifts_list_preference_update_failed",
					{
						status_code: response.status,
						response_data: response.data,
					},
					{
						user_id: userId,
						department_id: chosenDepartment.department_id,
						day: dayName,
						week_index: weekIndex,
						preference_type: preferenceType,
						action: isAlreadySelected ? "remove" : "add",
						error_type: "api_error",
					},
				);
			}
		} catch (error) {
			console.error("Error updating preference:", error);
			showErrorSnackbar(
				t("failed.update.preference"),
				{
					action: "failed to update preference",
					nurse_id: userId,
				},
				error,
			);
			trackErrorEvent("shifts_list_preference_update_failed", error, {
				user_id: userId,
				department_id: chosenDepartment.department_id,
				day: dayName,
				week_index: weekIndex,
				preference_type: preferenceType,
				action: isAlreadySelected ? "remove" : "add",
			});
		}
	};

	const getPreference = (weekIndex: number, dayIndex: number) => {
		return (
			preferences.find((p: DayPreference) => p.weekIndex === weekIndex && p.dayIndex === dayIndex) || {
				preferDay: false,
				preferNight: false,
			}
		);
	};

	return (
		<Box sx={{ width: "100%", maxWidth: 600, mx: "auto", py: 0, px: 2 }}>
			{Array(2)
				.fill(null)
				.map((_, weekIndex) => (
					<Box key={weekIndex} sx={{ mb: 4 }}>
						<Typography
							sx={{
								color: alpha(INK.SECONDARY, 0.5),
								fontSize: "0.875rem",
								fontWeight: 600,
								mb: 2,
								pl: 2,
							}}
						>
							{t("week")} {weekIndex + 1}
						</Typography>
						{Array(7)
							.fill(null)
							.map((_, dayIndex) => {
								const dayDate = weekStartDate.plus({ days: dayIndex });
								const dayName = dayDate.setLocale("en").toFormat("cccc"); // English name for backend
								const dayNameLocalized = weekdayNamesLocalized[dayIndex]; // Localized name for display
								const currentPreference = getPreference(weekIndex, dayIndex);

								return (
									<Box
										key={`${weekIndex}-${dayIndex}`}
										sx={{
											display: "flex",
											alignItems: "flex-start",
											mb: 2,
											p: 2,
											borderRadius: "8px",
											backgroundColor: "white",
											"&:hover": {
												backgroundColor: NEUTRAL[100],
											},
										}}
										onClick={() => {
											trackEvent("shifts_list_day_clicked", {
												user_id: userId,
												day: dayName,
												week_index: weekIndex,
												has_preference: currentPreference.preferDay || currentPreference.preferNight,
											});
										}}
									>
										<Box sx={{ flex: 1 }}>
											<Typography
												sx={{
													fontSize: "1rem",
													fontWeight: 600,
													color: INK.SECONDARY,
													mb: 0.5,
												}}
											>
												{dayNameLocalized}
											</Typography>
											<Box sx={{ display: "flex", gap: 1, mt: 1 }}>
												<ToggleButton
													value="preferDay"
													selected={currentPreference.preferDay}
													onChange={() =>
														handlePreferenceChange({
															weekIndex,
															dayIndex,
															preferenceType: ShiftType.Day,
														})
													}
													sx={{
														px: 1,
														py: 0.5,
														borderColor: NEUTRAL[300],
														"&.Mui-selected": {
															bgcolor: "#FBBB00 !important",
															color: "white !important",
														},
													}}
												>
													<WbSunnyOutlinedIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
													{t("prefer.day")}
												</ToggleButton>
												<ToggleButton
													value="preferNight"
													selected={currentPreference.preferNight}
													onChange={() =>
														handlePreferenceChange({
															weekIndex,
															dayIndex,
															preferenceType: ShiftType.Night,
														})
													}
													sx={{
														px: 1,
														py: 0.5,
														borderColor: NEUTRAL[300],
														"&.Mui-selected": {
															bgcolor: "#5F5CE8 !important",
															color: "white !important",
														},
													}}
												>
													<DarkModeOutlinedIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
													{t("prefer.night")}
												</ToggleButton>
											</Box>
										</Box>
									</Box>
								);
							})}
					</Box>
				))}
		</Box>
	);
}
